import React, { useState, useEffect } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  Home,
  Bot,
  Activity,
  Users,
  Settings,
  Bell,
  HelpCircle,
  ChevronDown,
  ChevronLeft,
  Sparkles,
  ArrowRight,
  Plus,
  Check,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import freshroadLogo from '../../images/freshroadlogo.png';
import DemoTutorialModal from './DemoTutorialModal';
import './demo.css';

const roleLabel = (user) => {
  if (!user) return '';
  if (user.userlevel >= 200) return 'Admin';
  if (user.userlevel >= 100) return 'Staff';
  return 'Member';
};

const displayName = (user) =>
  user?.name || user?.username || (user?.email ? user.email.split('@')[0] : 'Account');

const initialsOf = (name) =>
  name
    .split(/[\s._-]+/)
    .filter(Boolean)
    .map((p) => p[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

const NAV_ITEMS = [
  { label: 'Home', to: '/dashboard', icon: Home, end: true },
  {
    label: 'Conversational AI',
    to: '/dashboard/customer-ai',
    icon: Bot,
    landing: '/dashboard/customer-ai/agents',
    children: [
      { label: 'Script Building', to: '/dashboard/customer-ai/scripts' },
      { label: 'Knowledge & Connections', to: '/dashboard/customer-ai/knowledge' },
      { label: 'Phone Numbers', to: '/dashboard/customer-ai/phone-numbers' },
      { label: 'Testing', to: '/dashboard/customer-ai/testing' },
      { label: 'Deploy', to: '/dashboard/customer-ai/deploy' },
    ],
  },
  {
    // Rename freely — this parent groups the call/chat Logs and the Analytics (Insights).
    label: 'Activity',
    to: '/dashboard/activity',
    icon: Activity,
    children: [
      { label: 'Logs', to: '/dashboard/logs' },
      { label: 'Analytics', to: '/dashboard/insights' },
      { label: 'Analysis Reports', to: '/dashboard/analysis-reports' },
      { label: 'Analytics Setup', to: '/dashboard/analytics-setup' },
    ],
  },
  {
    label: 'Customers',
    to: '/dashboard/customers',
    icon: Users,
    children: [
      { label: 'CRM', to: '/dashboard/customers/crm' },
      { label: 'Calendar', to: '/dashboard/customers/calendar' },
    ],
  },
  { label: 'Settings', to: '/dashboard/settings', icon: Settings },
];

/* A collapsible section is active when its own path or any child path matches. */
const isSectionActive = (item, pathname) =>
  pathname.startsWith(item.to) || (item.children || []).some((c) => pathname.startsWith(c.to));

const DemoLayout = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [tutorialOpen, setTutorialOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [openMenus, setOpenMenus] = useState(() =>
    NAV_ITEMS.filter((n) => n.children && isSectionActive(n, location.pathname)).map((n) => n.to)
  );

  // Auto-collapse the profile / notification popovers whenever the route changes.
  useEffect(() => {
    setProfileOpen(false);
    setNotifOpen(false);
  }, [location.pathname]);

  const name = displayName(user);

  const handleTutorialNavigate = (route) => {
    if (route) navigate(route);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const toggleMenu = (to) =>
    setOpenMenus((open) => (open.includes(to) ? open.filter((t) => t !== to) : [...open, to]));

  return (
    <div className="demo-ui h-screen overflow-hidden bg-[#F7F9FB] text-slate-900">
      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-30 flex flex-col border-r border-slate-200 bg-white transition-all duration-200 ${
          collapsed ? 'w-[72px]' : 'w-[240px]'
        }`}
      >
        {/* Logo — full lockup when expanded; flower-only (cropped) when collapsed */}
        <div className={`flex h-[72px] items-center overflow-hidden ${collapsed ? 'justify-center px-2' : 'px-5'}`}>
          <img
            src={freshroadLogo}
            alt="fresh road"
            className={collapsed ? 'h-9 w-9 object-cover object-left' : 'h-8 object-contain'}
          />
        </div>

        {/* Nav */}
        <nav className="mt-2 flex-1 space-y-1 overflow-y-auto px-3">
          {NAV_ITEMS.map(({ label, to, icon: Icon, end, children, landing }) => {
            if (!children) {
              return (
                <NavLink
                  key={to}
                  to={to}
                  end={end}
                  title={collapsed ? label : undefined}
                  className={({ isActive }) =>
                    `flex items-center gap-3 rounded-xl px-3 py-2.5 text-[14px] font-medium transition-colors ${
                      isActive
                        ? 'bg-teal-50 text-teal-700'
                        : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                    } ${collapsed ? 'justify-center px-2' : ''}`
                  }
                >
                  <Icon size={19} strokeWidth={1.9} className="shrink-0" />
                  {!collapsed && <span>{label}</span>}
                </NavLink>
              );
            }

            const sectionActive = isSectionActive({ to, children }, location.pathname);
            const open = openMenus.includes(to);
            return (
              <div key={to}>
                <button
                  title={collapsed ? label : undefined}
                  onClick={() => {
                    if (collapsed) {
                      navigate(landing || children[0].to);
                    } else if (landing) {
                      navigate(landing);
                      setOpenMenus((o) => (o.includes(to) ? o : [...o, to]));
                    } else {
                      toggleMenu(to);
                    }
                  }}
                  className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-[14px] font-medium transition-colors ${
                    sectionActive
                      ? 'bg-teal-50 text-teal-700'
                      : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                  } ${collapsed ? 'justify-center px-2' : ''}`}
                >
                  <Icon size={19} strokeWidth={1.9} className="shrink-0" />
                  {!collapsed && (
                    <>
                      <span className="flex-1 text-left">{label}</span>
                      <ChevronDown
                        size={15}
                        className={`shrink-0 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`}
                      />
                    </>
                  )}
                </button>
                {!collapsed && open && (
                  <div className="mt-1 space-y-0.5 border-l border-slate-200 pl-3" style={{ marginLeft: '22px' }}>
                    {children.map((child) => (
                      <div key={child.to} className="flex items-center gap-1">
                        <NavLink
                          to={child.to}
                          className={({ isActive }) =>
                            `block flex-1 rounded-lg px-3 py-2 text-[13px] font-medium transition-colors ${
                              isActive
                                ? 'bg-teal-50 text-teal-700'
                                : 'text-slate-500 hover:bg-slate-50 hover:text-slate-900'
                            }`
                          }
                        >
                          {child.label}
                        </NavLink>
                        {child.addTo && (
                          <button
                            onClick={() => navigate(child.addTo)}
                            title={`New ${child.label.replace(/s$/, '')}`}
                            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-teal-700 hover:bg-teal-50"
                          >
                            <Plus size={16} strokeWidth={2.4} />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* Promo card */}
        {!collapsed && (
          <div className="mx-4 mb-4 rounded-2xl border border-slate-100 bg-slate-50/80 p-4">
            <Sparkles size={18} className="text-teal-500" />
            <p className="mt-2 text-[13px] font-semibold leading-snug">Need help getting started?</p>
            <p className="mt-1 text-[12px] leading-snug text-slate-500">
              Take the quick tour: build, test, deploy, and analyze your AI.
            </p>
            <button
              onClick={() => setTutorialOpen(true)}
              className="mt-3 inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[12px] font-semibold text-teal-700 hover:border-teal-200 hover:bg-teal-50"
            >
              Get Started <ArrowRight size={13} />
            </button>
          </div>
        )}

        {/* Collapse */}
        <button
          onClick={() => setCollapsed((c) => !c)}
          className={`mb-3 flex items-center gap-2 px-5 text-[13px] font-medium text-slate-500 hover:text-slate-800 ${
            collapsed ? 'justify-center px-2' : ''
          }`}
        >
          <ChevronLeft size={16} className={`transition-transform ${collapsed ? 'rotate-180' : ''}`} />
          {!collapsed && 'Collapse'}
        </button>

        {/* User & notifications — pinned to sidebar bottom */}
        <div className="relative border-t border-slate-100 p-3">
          {profileOpen && (
            <div
              className={`absolute bottom-full z-50 mb-2 rounded-xl border border-slate-200 bg-white p-1.5 shadow-lg ${
                collapsed ? 'left-2 w-60' : 'left-3 right-3'
              }`}
            >
              <div className="border-b border-slate-100 px-3 py-2">
                <p className="truncate text-[13px] font-semibold">{name}</p>
                <p className="truncate text-[11.5px] text-slate-500">{user?.email}</p>
              </div>
              <button
                onClick={() => {
                  setProfileOpen(false);
                  navigate('/dashboard/settings');
                }}
                className="block w-full rounded-lg px-3 py-2 text-left text-[13px] font-medium text-slate-600 hover:bg-slate-50"
              >
                Settings
              </button>
              <button
                onClick={() => {
                  setProfileOpen(false);
                  navigate('/legacy');
                }}
                className="block w-full rounded-lg px-3 py-2 text-left text-[13px] font-medium text-slate-600 hover:bg-slate-50"
              >
                Legacy dashboard
              </button>
              <button className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-[13px] font-medium text-slate-600 hover:bg-slate-50">
                <HelpCircle size={14} /> Help & support
              </button>
              <button
                onClick={handleLogout}
                className="block w-full rounded-lg px-3 py-2 text-left text-[13px] font-medium text-red-600 hover:bg-red-50"
              >
                Log out
              </button>
            </div>
          )}

          {notifOpen && (
            <div
              className={`absolute bottom-full z-50 mb-2 rounded-xl border border-slate-200 bg-white shadow-lg ${
                collapsed ? 'left-2 w-64' : 'left-3 right-3'
              }`}
            >
              <div className="flex items-center justify-between border-b border-slate-100 px-3.5 py-2.5">
                <p className="text-[13px] font-semibold">Notifications</p>
                <button onClick={() => setNotifOpen(false)} className="rounded-md p-1 text-slate-400 hover:bg-slate-100">
                  <ChevronDown size={14} />
                </button>
              </div>
              <div className="flex flex-col items-center gap-1.5 px-4 py-6 text-center">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-emerald-50 text-emerald-500">
                  <Check size={17} />
                </span>
                <p className="text-[13px] font-semibold">You're all caught up</p>
                <p className="text-[11.5px] text-slate-500">No new notifications right now.</p>
              </div>
              <button
                onClick={() => {
                  setNotifOpen(false);
                  navigate('/dashboard/logs');
                }}
                className="block w-full border-t border-slate-100 px-3.5 py-2.5 text-center text-[12.5px] font-semibold text-teal-700 hover:bg-slate-50"
              >
                View activity
              </button>
            </div>
          )}

          {collapsed ? (
            <button
              onClick={() => {
                setProfileOpen((o) => !o);
                setNotifOpen(false);
              }}
              title={name}
              className="mx-auto flex h-9 w-9 items-center justify-center rounded-full bg-teal-600 text-[12px] font-semibold text-white hover:opacity-90"
            >
              {initialsOf(name)}
            </button>
          ) : (
            <div className="flex items-center gap-1">
              <button
                onClick={() => {
                  setProfileOpen((o) => !o);
                  setNotifOpen(false);
                }}
                className="flex min-w-0 flex-1 items-center gap-2.5 rounded-xl px-1.5 py-1.5 hover:bg-slate-50"
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-teal-600 text-[12.5px] font-semibold text-white">
                  {initialsOf(name)}
                </span>
                <span className="min-w-0 flex-1 text-left leading-tight">
                  <span className="block truncate text-[13px] font-semibold">{name}</span>
                  <span className="block text-[11.5px] text-slate-500">{roleLabel(user)}</span>
                </span>
                <ChevronDown size={14} className="shrink-0 rotate-180 text-slate-400" />
              </button>
              <button
                onClick={() => {
                  setNotifOpen((o) => !o);
                  setProfileOpen(false);
                }}
                title="Notifications"
                className={`relative shrink-0 rounded-full p-2 hover:bg-slate-100 ${notifOpen ? 'text-teal-700' : 'text-slate-500'}`}
              >
                <Bell size={18} strokeWidth={1.8} />
              </button>
            </div>
          )}
        </div>
      </aside>

      {/* Main column — fills the viewport; pages manage their own internal scrolling */}
      <div className={`flex h-screen min-h-0 flex-col transition-all duration-200 ${collapsed ? 'ml-[72px]' : 'ml-[240px]'}`}>
        {/* Page content — never scrolls itself; long lists scroll inside their components */}
        <main className="min-h-0 flex-1 overflow-hidden px-8 pb-6 pt-2">
          <Outlet />
        </main>
      </div>

      {/* Getting-started guide — new UI styling */}
      <DemoTutorialModal
        open={tutorialOpen}
        onClose={() => setTutorialOpen(false)}
        onNavigate={handleTutorialNavigate}
      />

    </div>
  );
};

export default DemoLayout;
